<template>
  <TopBanner
    layout="normal"
    currentPage="Terms & Conditions"
    heading="Terms & Conditions"
    bannerClass="banner-booking"
  />

  <!-- Page wrapper -->
  <section class="py-16 md:py-24 overflow-visible">
    <div class="max-w-7xl mx-auto px-6 lg:px-10 xl:px-14 overflow-visible">
      <div class="grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14 items-start overflow-visible">
        <!-- TOC -->
        <aside
          class="hidden lg:block sticky self-start"
          :style="{ top: stickyTop + 'px' }"
          aria-label="On this page"
        >
          <h3 class="text-slate-900 text-sm font-semibold uppercase tracking-[0.16em] mb-4">
            On this page
          </h3>
          <nav class="text-slate-600 text-sm space-y-2">
            <a class="toc-link" href="#general">General</a>
            <a class="toc-link" href="#registration">Registration & User Agreement</a>
            <a class="toc-link" href="#service-agreements">Service Agreements</a>
            <a class="toc-link" href="#cancellation">Cancellation</a>
            <a class="toc-link" href="#payment">Payment of Fees</a>
            <a class="toc-link" href="#liability">Liability</a>
            <a class="toc-link" href="#final">Final Provisions</a>
          </nav>
        </aside>

        <!-- Content -->
        <article class="prose-area">
          <!-- Title lead -->
          <p class="lead">
            Terms and Conditions applicable to the Use of the Vehitra World Platform and to the Services provided by Transportation Service Providers
            (“Vehitra World T&C”)
          </p>

          <!-- General -->
          <section id="general" class="section-block">
            <h3>General</h3>
            <p>
              <strong>1. Scope of Application.</strong> These Terms and Conditions (“T&amp;C”) govern (i) the access and use of the websites
              <a class="inline-link" href="https://www.vehitraworld.com" target="_blank" rel="noopener">www.vehitraworld.com</a>,
              associated websites and other content, websites, products and services (jointly the “Platform”) made available or supplied by Vehitra World, and
              (ii) the arrangement of agreements on driver, mobility, and logistics services (“Services“) through the Platform or by phone or email with service
              providers co-operating with Vehitra World (“Service Providers”).
            </p>
            <p>
              <strong>2. About Vehitra World.</strong> Vehitra World arranges agreements on Services (“Service Agreements”) which are concluded between Users and
              Service Providers. “Users” are individuals and legal entities who use the Platform, with or without being registered.
            </p>
            <p>
              <strong>3. Acceptance of T&amp;C.</strong> By accessing, registering for, or using the Platform the User accepts these T&amp;C. Please read them carefully.
            </p>
            <p>
              <strong>4. Modification of T&amp;C.</strong> Vehitra World may modify these T&amp;C and will notify Users via the email stored for notifications.
              If the User does not object within four weeks, acceptance is deemed granted.
            </p>
            <p>
              <strong>5. No application of user terms.</strong> Terms of the User do not apply, even if Vehitra World does not explicitly object.
            </p>
            <p>
              <strong>6. Language.</strong> The language of the agreement is English.
            </p>
            <p>
              <strong>7. Availability.</strong> The current version of these T&amp;C is always displayed and can be printed or saved electronically.
            </p>
            <p>
              <strong>8. Data Privacy.</strong> Vehitra World and Service Providers collect and store personal data necessary to process transactions and observe
              legal requirements. See our
              <router-link class="inline-link" :to="{ name: 'PrivacyPolicy' }">Privacy Policy</router-link>.
            </p>
          </section>

          <!-- Registration -->
          <section id="registration" class="section-block">
            <h3>Registration for the Platform and the Platform User Agreement</h3>
            <p>
              <strong>1. Registration process.</strong> Registration is required to use the Platform, free of charge, and requires acceptance of these T&amp;C.
              Users provide details (e.g., name, email). By completing the request (web/app/phone/email), the User makes a binding offer for a Platform User
              Agreement. Vehitra World may accept or reject the offer and will confirm by email if accepted.
            </p>
            <p>
              <strong>2. Entitlement.</strong> Registration is possible for individuals with legal capacity, legal entities, and partnerships. Legal entities or
              partnerships must be registered by an authorized individual identified by name.
            </p>
            <p>
              <strong>3. Accuracy of details.</strong> Users must maintain accurate, up-to-date account details. Failure may lead to limited access or termination.
            </p>
            <p>
              <strong>4. Email communication.</strong> Legal statements may be delivered to the email stored in the account.
            </p>
            <p>
              <strong>5. Passwords.</strong> Users must keep passwords secret and not allow third-party use. If compromise is suspected, notify Vehitra World
              immediately; the account may be blocked during investigation.
            </p>
            <p>
              <strong>6. Liability for misuse.</strong> Users are liable for actions taken via their account unless they are not responsible for the misuse.
            </p>
            <p>
              <strong>7. Termination.</strong> The Platform User Agreement is indefinite. Users may terminate at any time by written notice (mail/hand delivery to
              Vehitra World Office 312, Building 60, Road 1701, Block 317 Diplomatic Area, Manama, Bahrain) or by email from the registered address to
              <a class="inline-link" href="mailto:bookings@vehitraworld.com">bookings@vehitraworld.com</a>. Vehitra World may also terminate by email notice.
            </p>
          </section>

          <!-- Service Agreements -->
          <section id="service-agreements" class="section-block">
            <h3>Service Agreements – Contractual Relationship &amp; Conclusion</h3>
            <p>
              <strong>1. Relationship.</strong> Vehitra World enables Users to arrange Services with licensed chauffeur/limousine companies. Service Agreements are
              concluded directly between the User and the Service Provider. Vehitra World acts strictly as an intermediary/broker and not as a transportation
              service provider.
            </p>
            <p>
              <strong>2. Conclusion.</strong> Users submit a booking request (service type, class, payment, billing and contact details). Submitting a Booking
              Request (via Platform/phone/email) is an offer to conclude a Service Agreement. The agreement comes into effect when Vehitra World sends “Ride
              Details” by email shortly before the ride with the Service Provider’s name and contact. If no provider can be found, the User is informed by email.
              Booking Conditions at the time of booking also apply.
            </p>
          </section>

          <!-- Cancellation -->
          <section id="cancellation" class="section-block">
            <h3>The Cancellation of Service Agreements</h3>
            <p>
              <strong>1. Conditions.</strong> Users may cancel free of charge or with costs according to the conditions applicable to the chosen booking class.
            </p>
            <p>
              <strong>2. Channel.</strong> Cancellations must be made through
              <a class="inline-link" href="https://www.vehitraworld.com" target="_blank" rel="noopener">www.vehitraworld.com</a>.
              Cancellations by email are not possible. Valid cancellations are confirmed by email.
            </p>
          </section>

          <!-- Payment -->
          <section id="payment" class="section-block">
            <h3>The payment of Fees due under the Service Agreements</h3>
            <p>
              <strong>1. Fees.</strong> All agreed fees include possible charges and taxes (if applicable). Unless agreed otherwise, route bookings apply to the
              agreed journey without stopovers or detours. In route/time bookings, the agreed fee remains due even if the passenger ends or shortens the journey.
            </p>
            <p>
              <strong>2. Invoices.</strong> Vehitra World sends invoices on behalf of the Service Provider by email.
            </p>
            <p>
              <strong>3. Payment method.</strong> Fees are paid via the method chosen during booking. For payments on invoice, the fee is payable within 10 days
              after notification.
            </p>
          </section>

          <!-- Liability -->
          <section id="liability" class="section-block">
            <h3>
              Liability of Vehitra World under the Platform User Agreement and of Service Providers under Service Agreements
            </h3>
            <p>
              <strong>1.</strong> Vehitra World is only liable towards the User according to the Platform User Agreement and arrangement obligations set out here.
              Vehitra World is not liable for actions/omissions of Service Providers under any Service Agreement. Service Providers/drivers are neither vicarious
              agents nor subcontractors of Vehitra World.
            </p>
            <p>
              <strong>2–4.</strong> Vehitra World and Service Providers assume unlimited liability for willful or grossly negligent damages, fraudulent
              non-disclosure, explicit guarantees, and personal injuries. Otherwise, liability is limited to typical, foreseeable damages for breaches of essential
              duties. Product liability remains unaffected.
            </p>
            <p>
              <strong>5–6.</strong> Vehitra World does not warrant obligations under arranged Service Agreements (including public-law compliance of Service
              Providers) and assumes no liability for free content/programs provided on the Platform.
            </p>
          </section>

          <!-- Final Provisions -->
          <section id="final" class="section-block">
            <h3>Final Provisions</h3>
            <p>
              <strong>1.</strong> Vehitra World may assign rights/obligations from the Platform User Agreement to a third party with 4 weeks’ prior notice; the
              User may then terminate for good cause.
            </p>
            <p>
              <strong>2. Force majeure.</strong> If events outside the control of Vehitra World or a Service Provider prevent performance (e.g., network/power
              outages, provider disruptions, severe weather), the affected party is relieved from performance during the disruption.
            </p>
            <p>
              <strong>2a. Governing law.</strong> These agreements are governed exclusively by the substantive law of the Federal Republic of Germany (excluding
              CISG and German international private law).
            </p>
            <p>
              <strong>2b. Venue.</strong> For Users who are registered business people, the venue for all disputes is Berlin.
            </p>
          </section>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import TopBanner from '@/components/TopBanner.vue'

const stickyTop = ref(104) // default; adjusted on mount to match your header

onMounted(() => {
  const header = document.querySelector('header')
  if (header) stickyTop.value = Math.round(header.getBoundingClientRect().height + 12)
})
</script>

<style scoped>
/* Base prose */
.prose-area {
  @apply text-slate-900; /* darker base text */
}

.prose-area p {
  @apply text-[15px] leading-7 md:text-[16px] md:leading-8 font-normal text-slate-800; /* stronger text contrast */
}

.prose-area h3 {
  @apply text-slate-900 text-xl md:text-2xl font-semibold;
}

.lead {
  @apply text-slate-900 md:text-lg font-semibold mb-6; /* darker lead paragraph */
}

/* Sections */
.section-block {
  @apply space-y-4 md:space-y-5;
  scroll-margin-top: 120px;
}
.section-block + .section-block {
  @apply pt-8 md:pt-10 border-t border-slate-200; /* slightly visible divider */
}

/* TOC */
.toc-link {
  @apply block px-3 py-2 rounded-lg hover:bg-slate-100 text-slate-700 hover:text-slate-900 transition;
  font-size: 14px;
}

/* Inline link style */
.inline-link {
  @apply text-emerald-700 hover:text-emerald-800 hover:underline font-medium;
}
</style>